/* eslint-disable max-lines */
import { Ruler } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "tailwind-variants";

import { Canvas, CanvasProductCompositionsCarousel } from "@design-lab/features/canvas";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Button } from "@design-lab/shared/ui";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { SparkleIcon } from "@design-lab/shared/ui/icons";
import { ProductBrowser } from "@design-lab/widgets/product-browser";
import { UploadButtons } from "./UploadButtons";

import type { ArtboardBaseProps, ArtboardLoadingState } from "./ArtboardBase.types";
import { SideSwitcher } from "./SideSwitcher";
import { TransfersPlaceholder } from "./TransfersPlaceholder";
import { Zoom } from "./Zoom";
import { LoadingBox } from "./loading-box";
import { RemoveBackground } from "./tool-bar";
import { BackgroundToggle } from "./tool-bar/BackgroundToggle";
import { ToolBar } from "./tool-bar/ToolBar";
import { TransfersRemoveBackground } from "./tool-bar/TransfersRemoveBackground";

/**
 * Generic Artboard Base Component
 *
 * This is a pure rendering component that accepts all data via props.
 * It has no knowledge of Design Lab or Transfers Lab specifics.
 * Use DesignLabArtboard or TransfersLabArtboard wrappers instead.
 */
export function ArtboardBase({
  readonly = false,
  canvasProps,
  controls,
  backgroundType,
  onBackgroundChange,
  loadingState,
  frontLoadingState,
  backLoadingState,
  currentSide,
  showPlaceholder = false,
  placeholderText = "Select an image to preview",
  zoomConfig = { initialZoom: 2, maxZoom: 10 },
  blurCanvas,
  previewURL,
  refineButton,
  productCarousel,
  mobileSizePlacementOpen = false,
  onMobileSizePlacementChange,
  isProductBrowserOpen,
  onProductBrowserClose,
  isSaveProject = false,
  uploadOverlay
}: ArtboardBaseProps) {
  // Only UI state hooks (no data-fetching)
  const [isMobileSizePlacementOpen, setIsMobileSizePlacementOpen] =
    useState(mobileSizePlacementOpen);

  // Sync with external control if provided
  const handleMobileSizePlacementChange = (open: boolean) => {
    setIsMobileSizePlacementOpen(open);
    onMobileSizePlacementChange?.(open);
  };

  // Add this effect to sync with parent state
  useEffect(() => {
    setIsMobileSizePlacementOpen(mobileSizePlacementOpen);
  }, [mobileSizePlacementOpen]);

  const { isDesktop } = useResponsive();

  // Helper to render a LoadingBox
  const renderLoadingBox = (state: ArtboardLoadingState) => (
    <LoadingBox
      imagePreview={state?.imagePreview}
      startTime={state?.startTime}
      estimatedDuration={loadingState?.estimatedDuration || ARTWORK_PROCESSING.ESTIMATED_DURATION}
      isDone={false}
    />
  );

  // Render canvas or placeholder
  const renderContent = () => {
    if (showPlaceholder) {
      return <TransfersPlaceholder text={placeholderText} />;
    }

    return (
      <>
        <div className={cn("h-full w-full", blurCanvas && "blur-sm")}>
          <Canvas
            {...(canvasProps as any)}
            backgroundType={backgroundType}
            readonly={readonly}
            onImageClick={canvasProps.labType === "design" ? canvasProps.onImageClick : undefined}
          />
        </div>
        {previewURL && canvasProps.labType === "transfers" && (
          <div
            className="absolute top-0 flex h-full w-full items-center justify-center"
            aria-hidden="true"
          >
            <img
              src={previewURL}
              alt="Preview"
              className={
                cn(
                  "h-4/5 w-4/5 object-contain transition-opacity duration-300",
                  loadingState?.isLoading ? "opacity-50" : "opacity-0"
                )!
              }
            />
          </div>
        )}
        {/* Loading box - supports both single (Transfers) and dual (Design Lab) modes */}
        {frontLoadingState || backLoadingState
          ? [
              { side: "front" as const, state: frontLoadingState },
              { side: "back" as const, state: backLoadingState }
            ].map(
              ({ side, state }) =>
                state?.isLoading && (
                  <div
                    key={side}
                    className="absolute left-1/2 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center p-2"
                    style={{ display: currentSide === side ? "flex" : "none" }}
                  >
                    {renderLoadingBox(state)}
                  </div>
                )
            )
          : loadingState?.isLoading && (
              <div className="absolute left-1/2 top-1/2 z-10 flex w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center p-2">
                {renderLoadingBox(loadingState)}
              </div>
            )}
      </>
    );
  };

  return (
    <Card className="h-full flex-1 overflow-hidden rounded-none lg:rounded-xl">
      <CardContent className="relative h-full p-0">
        {/* Side Switcher (Design Lab only) */}
        {controls.showSideSwitcher && <SideSwitcher />}

        {!isSaveProject && controls.showZoom && (
          <Zoom maxZoom={zoomConfig.maxZoom} initialZoom={zoomConfig.initialZoom} />
        )}

        {/* Background Toggle & Pick Model */}
        {!isMobileSizePlacementOpen && !readonly && controls.showBackgroundToggle && (
          <div className="absolute bottom-4 left-2 z-10 flex flex-col gap-2 lg:bottom-auto lg:left-auto lg:right-4 lg:top-20">
            <BackgroundToggle
              value={backgroundType}
              onValueChange={onBackgroundChange}
              labType={canvasProps.labType || "design"}
            />
          </div>
        )}

        {/* Size & Placement + Refine Button (Design Lab Mobile) */}
        {!isMobileSizePlacementOpen && !readonly && !isDesktop && controls.showSizePlacement && (
          <div className="absolute bottom-4 right-2 z-10 flex flex-col gap-2">
            <Button
              aria-label="Design Size & Placement"
              variant="outline"
              size="icon"
              data-testid="design-size-placement-open"
              className="h-14 w-12 flex-col items-center justify-center !gap-0 p-0"
              onPress={() => handleMobileSizePlacementChange(true)}
            >
              <div className="flex size-8 items-center justify-center">
                <Ruler className="size-7 stroke-[1.5px]" />
              </div>
              <div className="text-xs font-medium leading-none">Resize</div>
            </Button>
            {refineButton?.show && refineButton.hasArtworkId && (
              <Button
                aria-label="Edit Design"
                variant="outline"
                size="icon"
                data-testid="refine-artwork-open"
                className="h-14 w-12 flex-col items-center justify-center !gap-0 p-0"
                onPress={refineButton.onPress}
              >
                <div className="flex size-8 items-center justify-center">
                  <SparkleIcon className="min-h-8 min-w-8" />
                </div>
                <div className="text-xs font-medium leading-none">Edit</div>
              </Button>
            )}
          </div>
        )}

        {/* Canvas or Placeholder */}
        {renderContent()}

        {/* Upload Overlay Mobile only, when no artwork */}
        {uploadOverlay?.show && !isDesktop && (
          <div className="absolute left-1/2 top-[40%] z-20 w-[146px] -translate-x-1/2 -translate-y-1/2 md:w-48">
            <UploadButtons
              onDeviceClick={uploadOverlay.onDeviceUpload}
              getInputProps={uploadOverlay.getInputProps}
              onLibraryClick={uploadOverlay.onLibraryOpen}
              onCanvaFileReady={uploadOverlay.onCanvaFileReady}
              isMobileOverlay
            />
          </div>
        )}

        {/* Toolbar */}
        {controls.showToolbar && (
          <div className="z-11 absolute bottom-0 left-1/2 mb-[4px] -translate-x-1/2">
            <ToolBar />
          </div>
        )}

        {/* Remove Background (Transfers Lab only) */}
        {controls.showRemoveBackground && (
          <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2">
            {canvasProps.labType === "transfers" ? (
              <TransfersRemoveBackground />
            ) : (
              <RemoveBackground />
            )}
          </div>
        )}

        {/* Product Carousel (Design Lab Mobile) */}
        {productCarousel?.show &&
          !isDesktop &&
          !readonly &&
          productCarousel.compositions.length > 0 && (
            <CanvasProductCompositionsCarousel
              compositions={productCarousel.compositions}
              selectedId={productCarousel.selectedId}
              onSelect={productCarousel.onSelect}
              onAddMoreProduct={productCarousel.onAddMoreProduct}
            />
          )}

        {/* Product Browser (Design Lab only) */}
        {controls.showProductBrowser && (
          <ProductBrowser
            isOpen={isProductBrowserOpen || false}
            onClose={onProductBrowserClose || (() => {})}
          />
        )}
      </CardContent>
    </Card>
  );
}
